import { MetadataColumn, MetadataColumnGroup } from '../../../../../../models/metadata';
import { InternalTree, Tree } from './models/column-picker';

type IndexedTree = { [key: string]: InternalTree };
type ColumnType = 'filterable' | 'sortable' | 'searchable';

export class TreeBuilder {
  private readonly columnType: string | null = null;

  constructor(columnType: ColumnType | null = null) {
    this.columnType = columnType;
  }

  buildTree(columnGroups: MetadataColumnGroup[], columns: MetadataColumn[]): Tree[] {
    const groupMap = this.buildGroupMap(columnGroups);
    const rootNodes = this.buildGroupHierarchy(columnGroups, groupMap);
    this.insertColumns(columns, groupMap);
    const sortedTree = this.sortNodes(rootNodes);
    return this.cleanNodes(sortedTree);
  }

  private buildGroupMap(columnGroups: MetadataColumnGroup[]): IndexedTree {
    const map: IndexedTree = {};
    columnGroups.forEach(({ key, name, order }) => {
      map[key] = { key, name, order, isGroup: true, children: [], disabled: false };
    });
    return map;
  }

  private buildGroupHierarchy(columnGroups: MetadataColumnGroup[], groupMap: IndexedTree): InternalTree[] {
    const roots: InternalTree[] = [];
    columnGroups.forEach(({ key, parentKey }) => {
      const node = groupMap[key];
      if (parentKey) {
        const parent = groupMap[parentKey];
        if (parent) {
          parent.children!.push(node);
        }
      } else {
        roots.push(node);
      }
    });
    return roots;
  }

  private insertColumns(columns: MetadataColumn[], groupMap: IndexedTree) {
    const selectedColumns = this.columnType ? columns.filter(col => col[this.columnType as ColumnType]) : columns;
    const sortedColumns = selectedColumns.sort((a, b) => a.order - b.order);
    sortedColumns.forEach(({ key, name, order, groupKey }) => {
      const columnNode: InternalTree = { key, name, order, isGroup: false, disabled: false };
      const parentGroup = groupMap[groupKey];
      if (parentGroup) {
        parentGroup.children!.push(columnNode);
      }
    });
  }

  private sortNodes(nodes: InternalTree[]): InternalTree[] {
    const groups = nodes.filter(node => node.isGroup).sort((a, b) => a.order - b.order);
    for (const group of groups) {
      if (group.children && group.children.length > 0) {
        group.children = this.sortNodes(group.children);
      }
    }
    const columns = nodes.filter(node => !node.isGroup).sort((a, b) => a.order - b.order);
    return columns.concat(groups);
  }

  private cleanNodes(nodes: InternalTree[]): Tree[] {
    return nodes
      .map(({ children, isGroup, key, name, disabled }) => {
        const cleaned = children && children.length ? this.cleanNodes(children) : undefined;
        return isGroup && (!cleaned || cleaned.length === 0)
          ? null
          : { key, name, disabled, ...(cleaned ? { children: cleaned } : {}) };
      })
      .filter((node): node is Tree => node !== null);
  }
}
